/**
 * Documents (ADR 0020): the employee's own, the administrator's side, reads and storage.
 */
import { api, get } from '@/api/client';
import { useQuery } from '@tanstack/react-query';

/* ── documents (ADR 0020) ──────────────────────────────────────────────────── */
export interface ApiDocument {
  id: number;
  scope: 'shared' | 'user';
  name: string;
  contentType: string | null;
  size: number;
  version: number;
  createdAt: string;
}

/**
 * What the company has published, and what has been filed for me.
 *
 * One route for both, because the server decides what I may see; the old
 * /files/:userId/* routes made the client name a folder, and every client named
 * the literal string "user" (web-employees.md §8).
 */
export function useMyDocuments() {
  return useQuery({
    queryKey: ['documents'],
    queryFn: () => get<{ shared: ApiDocument[]; mine: ApiDocument[] }>('/documents'),
    staleTime: 30_000,
  });
}

/**
 * Download one. The browser can carry the token, so this streams through the API
 * and the storage key never leaves the server; the phone uses a signed link
 * instead, because it has no viewer that sends a header.
 */
export async function downloadDocument(doc: ApiDocument): Promise<void> {
  const { API_BASE } = await import('@/config');
  const { getToken } = await import('@/auth/store');
  const r = await fetch(`${API_BASE}/documents/${doc.id}/download`, {
    headers: { Authorization: `Bearer ${getToken() ?? ''}` },
  });
  if (!r.ok) throw new Error(`${r.status}`);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.name;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── documents, the administrator's side (ADR 0020) ───────────────────────── */
/** What one person has on file, or what the company has published when userId is null. */
export function useAdminDocuments(userId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['admin-documents', userId],
    queryFn: () =>
      get<ApiDocument[]>(userId == null ? '/admin/documents' : `/admin/documents?userId=${userId}`),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export interface ApiDocumentReads {
  scope: 'shared' | 'user';
  /** For a published document, the people who have read it. For a private one, every opening. */
  entries: Array<{ userId: number; name: string | null; readAt: string }>;
  /** Only for a published document: who still has to read it. */
  outstanding: Array<{ userId: number; name: string }>;
}

/**
 * Who has opened a document.
 *
 * Two shapes, because the two questions are different. A published document
 * answers "who still has to read this", counting current memberships, so the
 * number can reach the total. A private one answers "who opened my file, and
 * when", every time, which is the employee's to see (ADR 0020, 9 and 10).
 */
export function useDocumentReads(id: number | null) {
  return useQuery({
    queryKey: ['document-reads', id],
    queryFn: () => get<ApiDocumentReads>(`/documents/${id}/reads`),
    enabled: id != null,
    retry: false,
  });
}

export interface ApiStorageUsage {
  usedBytes: number;
  allowanceBytes: number;
  employees: number;
  gbPerEmployee: number;
  percent: number;
}

/** What the company has used of its document storage, and what it may use. */
export function useDocumentUsage(enabled = true) {
  return useQuery({
    queryKey: ['document-usage'],
    queryFn: () => get<ApiStorageUsage>('/admin/documents/usage'),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/** How a name clash is resolved, when the server says there is one. */
export type OnConflict = 'new-version' | 'correction' | 'keep-both';

/**
 * File a document for one person, or publish one to the company.
 *
 * Answers 409 with `code: 'name_exists'` when the name is taken; the caller asks
 * which of the three it is and sends it back as onConflict (ADR 0020, 11 and 12).
 */
export async function fileDocument(opts: {
  file: File;
  ownerUserId: number | null;
  notify: boolean;
  onConflict?: OnConflict | undefined;
}): Promise<ApiDocument> {
  const fd = new FormData();
  fd.append('file', opts.file);
  fd.append('scope', opts.ownerUserId == null ? 'shared' : 'user');
  if (opts.ownerUserId != null) fd.append('ownerUserId', String(opts.ownerUserId));
  fd.append('notify', String(opts.notify));
  if (opts.onConflict) fd.append('onConflict', opts.onConflict);
  return api<ApiDocument>('/admin/documents', { method: 'POST', body: fd });
}

/** Remove a misfiling. The reason is stored with it and is not optional. */
export async function removeDocument(id: number, reason: string): Promise<void> {
  await api<unknown>(`/admin/documents/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}
